import React, { useEffect } from "react";
import { BUTTON_LABEL, LABELS } from "../../constants";

interface ImageItem {
  _id?: string;
  image?: string | { _id: string };
  fileName?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  sizeBytes?: number;
  uploadedBy?: { _id: string; name?: string; firstName?: string; lastName?: string };
  uploadedAt?: string;
  isPurged?: boolean;
}

interface ImageLightboxProps {
  images: ImageItem[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  backendUrl: string;
}

const ImageLightbox = ({
  images,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  backendUrl,
}: ImageLightboxProps) => {
  const currentImage = images[currentIndex];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  if (!currentImage) return null;

  const imageUrl = currentImage.imageUrl?.startsWith("http")
    ? currentImage.imageUrl
    : `${backendUrl}${currentImage.imageUrl}`;

  const isPurged = currentImage.isPurged;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPurged || !imageUrl) return;
    const filename = currentImage.fileName || `image-${currentIndex + 1}.jpg`;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = filename;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="image-lightbox-overlay" onClick={onClose}>
      <div className="image-lightbox-top-right-bar" onClick={(e) => e.stopPropagation()}>
        {!isPurged && (
          <button
            type="button"
            className="image-lightbox-download-btn"
            onClick={handleDownload}
            title={LABELS.DOWNLOAD_IMAGE_TEXT}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: "6px" }}
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            {BUTTON_LABEL.DOWNLOAD}
          </button>
        )}
        <button className="image-lightbox-close" onClick={onClose} title="Close">
          &times;
        </button>
      </div>

      <div className="image-lightbox-container" onClick={(e) => e.stopPropagation()}>
        {images.length > 1 && (
          <>
            <button
              className="image-lightbox-prev"
              onClick={onPrev}
              disabled={currentIndex <= 0}
            >
              &#8249;
            </button>
            <button
              className="image-lightbox-next"
              onClick={onNext}
              disabled={currentIndex >= images.length - 1}
            >
              &#8250;
            </button>
          </>
        )}

        <div className="image-lightbox-content">
          {isPurged ? (
            <div className="image-lightbox-expired">
              <p>{LABELS.IMAGES_EXPIRED_TEXT}</p>
            </div>
          ) : (
            <img src={imageUrl} alt={currentImage.fileName || "Reference"} />
          )}
          <div className="image-lightbox-info">
            <p className="image-lightbox-filename">
              {currentImage.fileName || `Image ${currentIndex + 1}`}
            </p>
            <p className="image-lightbox-counter">
              {currentIndex + 1} / {images.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;
